/**
 * The docs hub snapshot as `github-insights` serves it (`/docs-hub`), read
 * by the hub and by the navigation's Docs count (DX-306). The shape is the
 * backend's `DocsHubSnapshot`; it is restated here rather than imported, the
 * way the deployments snapshot is, because the app does not depend on a
 * backend plugin.
 */

export type DocsPageType = 'Decision' | 'Tutorial' | 'Runbook';

export type DocsPage = {
  title: string;
  /** The page's address in the TechDocs reader. */
  location: string;
  site: string;
  section: string;
  type?: DocsPageType;
  /** ISO date of the last commit to the page's source file. */
  lastChanged: string;
};

export type DocsCategory =
  | 'architecture'
  | 'decisions'
  | 'handbook'
  | 'tutorials'
  | 'serviceDocs'
  | 'specs';

export type DocsHubSnapshot = {
  generatedAt: string | null;
  freshness: { thresholdDays: number; source: string };
  totals: { pages: number; sites: number };
  categories: Record<DocsCategory, number>;
  recent: { all: DocsPage[]; decisions: DocsPage[]; runbooks: DocsPage[] };
  needsReview: { count: number; pages: DocsPage[] };
  unreadSites: string[];
};

const DAY = 86_400_000;

/** Whole days since a page last changed. */
export const daysSince = (iso: string, now = Date.now()): number =>
  Math.max(0, Math.floor((now - Date.parse(iso)) / DAY));

/**
 * How long ago something changed, at the coarseness a list of pages needs:
 * minutes within the hour, hours within the day, days after that.
 */
export const changedAgo = (iso: string, now = Date.now()): string => {
  const ms = Math.max(0, now - Date.parse(iso));
  if (ms < 60 * 60 * 1000) {
    return `${Math.max(1, Math.round(ms / 60_000))} min ago`;
  }
  if (ms < DAY) {
    return `${Math.round(ms / 3_600_000)} h ago`;
  }
  return `${Math.floor(ms / DAY)} d ago`;
};

/**
 * A page's place, as the lists print it under its title: the section for a
 * page of Atlas, whose sections are the hub's categories; the site, for a
 * service's own documentation.
 */
export const placeOf = (page: DocsPage): string =>
  page.site === page.section ? page.site : `${page.site} · ${page.section}`;

/** A page's path within its site, read off its address in the reader. */
export const pathInSite = (page: DocsPage): string =>
  // `/docs/<namespace>/<kind>/<name>/<path>/`
  page.location.split('/').slice(5).join('/').replace(/\/+$/, '') || 'index';

/**
 * Each listed page's place, told apart where two rows would read the same.
 *
 * Two pages of one site can share a heading — the brand site has two called
 * "Brand Guidelines" — and with the same title and the same place they were
 * two identical rows leading to different pages. Those, and only those, carry
 * their path in the site as well; every other row keeps its plain place.
 */
export const placesOf = (pages: DocsPage[]): string[] => {
  const key = (page: DocsPage) => `${page.title}\u0000${placeOf(page)}`;
  const seen = new Map<string, number>();
  for (const page of pages) {
    seen.set(key(page), (seen.get(key(page)) ?? 0) + 1);
  }
  return pages.map(page =>
    (seen.get(key(page)) ?? 0) > 1
      ? `${placeOf(page)} · ${pathInSite(page)}`
      : placeOf(page),
  );
};
